<template lang="pug">
	.creative-detail--header
		.flex-col.space-large.creative-header-data
			.flex-row.justify.wrap.nav-header-wrapper.space-medium(v-if="creative")
				.flex-col.center.align-center.grey(v-if="isTemporary")
					fas(icon="hourglass-half", size="2xl", :title="$tCaption('This is a temporary creative and will be removed after some time.')")
				.flex-col.nav-header.justify.flex-1
					.flex-row.space.justify.align-center
						.left.flex-row.space.align-center
							a(v-if="creative.IsTemplate && creative.IsProduct", @click="() => $router.push({ name: 'products-creatives-templates-list' })") {{$tCaption('Adthos Products')}} / {{$tCaption('Creative Templates')}}
							a(v-else @click="() => isNetworkStudio ? gotoBackLink() : $router.push({ name: 'creative-list' })") Adthos {{$auth.hasCreativeApi() ? (isNetworkStudio ? $tCaption('Network Studio') : $tCaption('Creative Studio')) : $tCaption('Creative Cloud')}}
							.spacer /
							span.nowrap {{$tCaption('Edit', '@actions')}}
							.edit-creatives.flex-row.space-large.flex-1(v-if="!isPublishMode")
								DxButton(v-if="!inMemory"
									:icon="isEditingCreative ? 'check' : 'edit'",
									:type="isEditingCreative ? 'success' : 'normal'"
									@click="processEditingCreative",
									:disabled="(isEditingCreative && readonly) || isSavingCreative"
								)
								DxButton(
									v-if="isEditingCreative",
									icon="close",
									type="normal"
									@click="cancelEditingCreative"
								)
							.loader-holder.flex-row.align-center
								Loader(v-if="creativeIsBusy", text="")
						.right.flex-row.space.align-center(v-if="!isPublishMode")
							Loader(v-if="sendingValidationRequest", :text="$tCaption('Sending emails..')")
							.small
								DxButton.small(
									v-if="isEditingCreative && creative.Oid && !creative.__isDeleting",
									icon="fas fa-database",
									:hint="$tCaption('Services manager')",
									:disabled="!creative || isPublishing || creative.IsPublished || readonly",
									@click="openServicesManager"
								)
							DxButton(v-if="isVoiceover", :text="$tCaption('Complete voiceover')", @click="$refs.voiceover.finish(userRestrictedAccess.voiceoverRequestId)", icon="fas fa-arrow-right")
							CreativeNewApproval(v-if="!inMemory && !userRestrictedAccess", :creative="creative", :tracker="_refs.tracker", :base64="finalAudioBase64")
							DxButton(v-if="isEditingCreative && creative.Oid && !creative.__isDeleting", icon="trash",:text="$tCaption('Delete')", type="danger"
								:disabled="!creative || isPublishing || creative.IsPublished || readonly", @click="deleteEditingCreative")
							DxDropDownButton.adt-menu(v-if="dropDownMenu.length", stylingMode="filled", icon="more", :data-source="dropDownMenu", display-expr="text", key-expr="id", :use-select-mode="false", :showArrowIcon="false", 
								:drop-down-options="{ width: 'auto' }", @item-click="onMenuClick", :onContentReady="onDDContentReady")
							Loader(v-if="creative.__isDeleting", text="")
					.flex-row.space.align-center(v-if="!creative.__isDeleting")
						.font-small {{(!creative.RootCreativeId || creative.RootCreativeId === creative.Oid) ? creative.Name : creative.RootCreativeName}}
						CreativeRevisions.audio-tracker(v-if="!inMemory && !$auth.isPublishMode()", ref="revisions", :creative="creative")
						.flex-row.align-center.space(v-if="creativeIsLockedByOtherUser")
							.creative-editing-lock {{$tCaption('This creative is being edited by')}} {{editingLock && editingLock.userName}}
							.font-small.audio-tracker-button.flex-row.center.align-center.nowrap(@click="requestAccessToCreative")
								fas(icon="lock-open")
								span {{$tCaption('Request access')}}
						.creative-editing-lock(v-else-if="creative.IsPublished && isEditingCreative") {{$tCaption('This creative is in read-only mode since it has been published, if you need to edit it start a revision.')}}
			CreativeDetail(
				v-if="isEditingCreative && editingCreative && !$auth.isPublishMode()",
				ref="creativeDetail",
				:editingCreative="editingCreative",
				:creative="creative",
				:readonly="readonly",
				:isLoading="isLoading",
				:languages="languages",
				:creativeIabCategoriesStr="creativeIabCategoriesStr",
				:creativeIabCategories="creativeIabCategories",
				:canSetLanguage="loadedTracks && !loadedTracks.length",
				:isCreativeFile="isCreativeFile",
				:insertions="allTracks.map(f => f.insertion)",
				:tracker="_refs.tracker",
				@add-language="$refs.revisions.showNewLanguagePopup()",
				@edit-insertion="ins => editInsertion(ins.Oid)",
				@openIabCategoriesDialog="openIabCategoriesDialog",
				@regenerate-insertion="regenerateInsertion"
			)
			Popup(v-if="sendEmailPopup.visible", :popupVisible.sync="sendEmailPopup.visible", :defaultActions="true", :onSuccess="sendEmails", :validate="() => !!sendEmailPopup.emails.length", 
				:width="480", :height="420", :successText="$tCaption('Send')", :title="$tCaption('Email creative')", :busy="!!sendEmailPopup.isSending")
				.flex-col.space-medium
					Loader(v-if="sendEmailPopup.isSending", text="", :fullscreen="true")
					.flex-col
						.font-lg {{$tCaption('Message')}}
						DxTextArea(:value.sync="sendEmailPopup.message", :height="65", stylingMode="filled")
					.flex-col.space-medium
						.flex-col
							.font-lg {{$tCaption('Recipients')}}
							DxTextBox(ref="email", @value-changed="e => onEmailAdd(e, e.value)", mode="email", :placeholder="$tCaption('Please type destination email address')", stylingMode="filled")
						.flex-row.space-medium.wrap
							.email-item.flex-row.align-center.space(v-for="(email, idx) in sendEmailPopup.emails")
								.adt-button.rounded.padded.flex-row.align-center.center.clickable.hoverable(@click="sendEmailPopup.emails.splice(idx, 1)")
									fas(icon="times")
								span {{email}}
		CreativeVoiceover(v-if="creative && !$auth.isPublishMode()", ref="voiceover", :creative="creative")
		CreativeProducerRequest(v-if="creative && !$auth.isPublishMode()", ref="producerRequest", :creative="creative")
</template>

<script>	
	import CreativeDetail from './CreativeDetail.vue';
	import CreativeRevisions from './CreativeRevisions.vue';
	import DxButton from 'devextreme-vue/button';
	import Loader from 'app-core/Loader.vue';
	import CreativeNewApproval from './CreativeNewApproval.vue';
	import DxDropDownButton from 'devextreme-vue/drop-down-button';
	import Popup from 'app-core/Popup.vue';
	import DxTextBox from 'devextreme-vue/text-box';
	import DxTextArea from 'devextreme-vue/text-area';
	import { DxValidator, DxEmailRule } from 'devextreme-vue/validator';
	import CreativeVoiceover from './CreativeVoiceover.vue';
	import CreativeProducerRequest from './CreativeProducerRequest.vue';

	export default {
		translationCategory: 'creative.Detail',
		name: 'creative-detail--header',
		props: [
			'creative', 'editingCreative', 'updateCreative', 'loadCreative', 'isNetworkStudio', 'isPublishMode', 'isEditingCreative', 'processEditingCreative', 'readonly', 'isSavingCreative', 'cancelEditingCreative',
			'creativeIsBusy', 'sendingValidationRequest', 'isPublishing', 'openServicesManager', 'downloadBrief', 'validationDueDate', 'inMemory', 'sendValidationRequestsButtonEnabled', 'sendValidationRequests',
			'makeCreativeCopy', 'deleteEditingCreative', 'creativeIsLockedByOtherUser', 'editingLock', 'isLoading', 'languages', '_refs', 'creativeIabCategoriesStr', 'creativeIabCategories', 'loadedTracks',
			'isCreativeFile', 'allTracks', 'openIabCategoriesDialog', 'regenerateInsertion', 'finalAudioBase64', 'gotoBackLink', 'publicationStatistic', 'translatedTracks', 'requestAccessToCreative', 'userRestrictedAccess', 'isVoiceover',
			'isTemporary', 'makeCreativePersistent'
		],
		components: {
			CreativeDetail, CreativeRevisions, DxButton, Loader, CreativeNewApproval, DxDropDownButton, Popup, DxTextBox, DxValidator, DxEmailRule, DxTextArea, CreativeVoiceover, CreativeProducerRequest
		},

		data() {
			return {
				sendEmailPopup: {
					message: null,
					emails: [],
					visible: false,
					isSending: false
				}
			}
		},
		computed: {
			dropDownMenu() {
				const items = [];

				if (this.validationDueDate !== null && !this.sendingValidationRequest && !this.inMemory) {
					items.push({
						id: 'validateSsml', text: this.$tCaption('Send Emails To Validate SSML texts'), icon: 'fas fa-envelope', disabled: !this.sendValidationRequestsButtonEnabled
					})
				}

				if (this.creative && !this.inMemory) {
					items.push(...[
						{ id: 'vast', text: this.$tCaption('Get VAST Tag'), icon: 'fas fa-copy', disabled: !this.creative.IsPublished },
						{ id: 'copy', text: this.$tCaption('Make a copy'), icon: 'fas fa-clone', disabled: !!this.userRestrictedAccess },
						...(this.translatedTracks?.length ? [{ id: 'translationsQA', text: this.$tCaption('Translations QA'), icon: 'fas fa-language' }] : []),
						{ id: 'requestVoiceover', text: this.$tCaption('Request voiceover'), icon: 'fas fa-microphone-lines', disabled: !!this.userRestrictedAccess },
						{ id: 'producerRequest', text: this.$tCaption('Ask Expert'), icon: 'fas fa-person-circle-question', disabled: !!this.userRestrictedAccess },
						{ id: 'creativeSettings', text: this.$tCaption('Settings'), icon: 'fas fa-gear' },
					])
				}

				if (this.creative && !this.inMemory && this.creative.IsPublished && this.publicationStatistic?.LastSession) {
					items.push(...[
						{ id: 'sendEmails', text: this.$tCaption('Email creative'), icon: 'fas fa-envelope' },
					]);
				}

				if (this.creative && this.creative.JsonConfig && this.creative.JsonConfig.brief) {
					items.push({
						id: 'brief', text: this.$tCaption('Download brief'), icon: 'fas fa-download'
					})
				}

				if (this.isTemporary) {
					items.push({
						id: 'makeCreativePersistent', text: this.$tCaption('Make persistent'), icon: 'fas fa-clock-rotate-left'
					})
				}
				
				return items;
			}
		},
		mounted() {
			if (__DEBUG__)
				window.header = this;
		},
		methods: {
			onEmailAdd(e, email) {
				if (!email || this.sendEmailPopup.emails.includes(email)) return;
				this.sendEmailPopup.emails.push(email);
				this.$refs.email.instance.option('value', null);
			},
			async sendEmails() {
				this.sendEmailPopup.isSending++;
				return this.$Data.postExternalApi('PublicationSession/SendEmail', {
					CreativeId: this.creative?.Oid,
					PublicationSessionId: this.publicationStatistic?.LastSession?.SessionId,
					Recipients: this.sendEmailPopup.emails.join(';'),
					EmailText: this.sendEmailPopup.message
				}).then((r) => {
					if (r?.Success !== false) {
						this.$notifySuccess('Email successfully sent');
					}
				}).finally(() => {
					this.sendEmailPopup.isSending--;
				});
			},
			onMenuClick(e) {
				switch(e.itemData.id) {
					case 'brief':
						this.downloadBrief();
						break;
					case 'validateSsml':
						this.sendValidationRequests();
						break;
					case 'vast':
						this._refs.vastLink.open(this.creative.Oid);
						break;
					case 'creativeSettings':
						this._refs.creativeSettings.show();
						break;
					case 'requestVoiceover':
						this.$refs.voiceover.show();
						break;
					case 'producerRequest':
						this.$refs.producerRequest.show();
						break;
					case 'copy':
						this.makeCreativeCopy();
						break;
					case 'sendEmails':
						this.sendEmailPopup.visible = false;
						this.sendEmailPopup.emails = [];
						this.sendEmailPopup.message = null;
						this.$nextTick(() => this.sendEmailPopup.visible = true);
						break;
					case 'translationsQA':
						this._refs.translationsQA.show();
						break;
					case 'makeCreativePersistent':
						this.$confirm(void 0, this.$tCaption('Make creative persistent?')).then(() => this.makeCreativePersistent());
						break;
				}
			},
			onDDContentReady(e) {
				const content = e?.component?._popup?.content();
				if (content?.classList) {
					content.classList.add('adt-menu');
				}
			}
		}
	};
</script>
<style lang='scss'>

</style>
